/**
 * ViewModeContext — single source of truth for "Mobile vs Desktop" rendering.
 *
 * Locked May 16, 2026 (LATE) by owner directive.
 * Locked 2026-05-31 — User-Blocker Pass: viewport meta tag + html class swap
 * are now executed SYNCHRONOUSLY on setMode (via a layout effect) so toggle
 * never gets stuck in a transitional state, and the chosen layout is in
 * effect before the surface re-renders. Surface chrome can no longer race
 * the meta tag.
 *
 *   mode          ∈  'auto' | 'mobile' | 'desktop'
 *   effectiveMode ∈  'mobile' | 'desktop'   (auto → derived from viewport)
 */
import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState, useCallback } from 'react';

const ViewModeContext = createContext(null);

const STORAGE_KEY = 'view_mode_preference';
const BREAKPOINT_PX = 768; // matches Tailwind `md`

const MOBILE_VIEWPORT  = 'width=device-width, initial-scale=1, viewport-fit=cover';
const DESKTOP_VIEWPORT = 'width=1280, initial-scale=0.4, minimum-scale=0.25, maximum-scale=5, user-scalable=yes';

const readStoredMode = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'mobile' || v === 'desktop' || v === 'auto') return v;
  } catch { /* ignore */ }
  return 'auto';
};

const detectViewport = () => {
  if (typeof window === 'undefined') return 'desktop';
  return window.innerWidth >= BREAKPOINT_PX ? 'desktop' : 'mobile';
};

const applyViewportMeta = (effectiveMode) => {
  if (typeof document === 'undefined') return;
  let tag = document.querySelector('meta[name="viewport"]');
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', 'viewport');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', effectiveMode === 'desktop' ? DESKTOP_VIEWPORT : MOBILE_VIEWPORT);
};

export const ViewModeProvider = ({ children }) => {
  const [mode, setModeState] = useState(readStoredMode);
  const [viewport, setViewportState] = useState(detectViewport);

  // Listen to viewport changes only when in auto mode
  useEffect(() => {
    const onResize = () => setViewportState(detectViewport());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const effectiveMode = mode === 'auto' ? viewport : mode;

  // useLayoutEffect — runs BEFORE the browser paints, so the html class +
  // viewport meta are already correct when the new surface renders. Prevents
  // the "stuck layout" / "swipe-left on desktop" race the user reported
  // 2026-05-31.
  useLayoutEffect(() => {
    const html = document.documentElement;
    html.classList.remove('vm-mobile', 'vm-desktop', 'vm-auto');
    html.classList.add(`vm-${effectiveMode}`);
    html.setAttribute('data-view-mode', mode);
    html.setAttribute('data-effective-view-mode', effectiveMode);
    applyViewportMeta(effectiveMode);
  }, [mode, effectiveMode]);

  const setMode = useCallback((next) => {
    if (next !== 'auto' && next !== 'mobile' && next !== 'desktop') return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    setModeState(next);
  }, []);

  const value = useMemo(() => ({
    mode,                     // user's stored preference
    effectiveMode,            // what's actually rendering
    isMobile: effectiveMode === 'mobile',
    isDesktop: effectiveMode === 'desktop',
    isAuto: mode === 'auto',
    setMode,
  }), [mode, effectiveMode, setMode]);

  return (
    <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>
  );
};

/**
 * Safe hook — if the Provider isn't mounted (e.g., during tests), defaults to
 * "auto" + viewport detection. Never throws.
 */
export const useViewMode = () => {
  const ctx = useContext(ViewModeContext);
  if (ctx) return ctx;
  const fallbackMode = detectViewport();
  return {
    mode: 'auto',
    effectiveMode: fallbackMode,
    isMobile: fallbackMode === 'mobile',
    isDesktop: fallbackMode === 'desktop',
    isAuto: true,
    setMode: () => { /* no-op */ },
  };
};

export default ViewModeContext;
